// Owner-only display currency. Every monetary value in the database is USD and stays USD.
// When the owner picks AUD, amounts are multiplied by the cached USD->AUD rate at RENDER
// time only — nothing stored is ever converted. Workers/managers never load this module's
// preference (the endpoints are owner-only), so their views always show plain USD.
import { useEffect, useReducer } from "react";
import axios from "axios";
import { API, authHeaders } from "@/constants/roles";

let state = { currency: "USD", rate: null, rateUpdatedAt: null };
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

export const getCurrencyState = () => state;

/** Fetch the owner's saved preference once when the admin console mounts. */
export async function initDisplayCurrency() {
  try {
    const r = await axios.get(`${API}/admin/display-currency`, { headers: authHeaders() });
    state = {
      currency: r.data.currency === "AUD" && r.data.rate ? "AUD" : "USD",
      rate: r.data.rate ?? null,
      rateUpdatedAt: r.data.rate_updated_at ?? null,
    };
  } catch {
    // not an owner session or network hiccup — stay on USD
  }
  emit();
  return state;
}

/** Persist a new preference (owner-only endpoint). Throws on failure. */
export async function setDisplayCurrency(currency) {
  const r = await axios.put(`${API}/admin/display-currency`, { currency }, { headers: authHeaders() });
  state = {
    currency: r.data.currency,
    rate: r.data.rate ?? null,
    rateUpdatedAt: r.data.rate_updated_at ?? null,
  };
  emit();
  return state;
}

/** Convert a stored USD amount to the active display currency. */
export const convertUsd = (v) => {
  const n = Number(v);
  if (v === null || v === undefined || v === "" || Number.isNaN(n)) return null;
  return state.currency === "AUD" && state.rate ? n * state.rate : n;
};

/** Money stored in USD -> display string like "$3,975.00 USD" / "$5,732.09 AUD". */
export function fmtMoney(v, digits = 2) {
  const n = convertUsd(v);
  if (n === null) return "—";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits })} ${state.currency}`;
}

/** Big numbers with thousands separators: 1381982 -> "1,381,982". */
export function fmtNum(v, digits = 0) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Subscribe a component to currency changes (toggling re-renders subscribers). */
export function useCurrency() {
  const [, force] = useReducer((x) => x + 1, 0);
  useEffect(() => {
    listeners.add(force);
    return () => listeners.delete(force);
  }, []);
  return state;
}
